<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
    overlay-opacity="0.85"
    content-class="modern-profile-info-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header -->
      <v-card-title class="editor-header d-flex justify-space-between align-center px-6 py-4">
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-account-details</v-icon>
          Edit Profile Information
        </div>
        <v-btn icon @click="onClose" class="close-btn" color="white" depressed large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Update your contact information and demographic details
        </div>
      </v-card-text>

      <!-- Messages -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="messageType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Form -->
      <v-card-text class="px-6 py-2">
        <v-row>
          <!-- First Name -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.first_name"
              label="First Name*"
              outlined
              dense
              prepend-inner-icon="mdi-account"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- Last Name -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.last_name"
              label="Last Name*"
              outlined
              dense
              prepend-inner-icon="mdi-account"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- Email -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.email"
              label="Email Address*"
              outlined
              dense
              prepend-inner-icon="mdi-email-outline"
              :rules="[rules.required, rules.email]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- Phone -->
          <v-col cols="12" md="6" class="py-2">
            <AppPhoneNumberInput
              v-model="profile.phone"
              @input="onChange"
              @update="onUpdate"
              class="modern-phone-input"
            />
            <div v-if="phonePayload.isValid === false" class="text-caption error--text mt-1">
              Please enter a valid phone number
            </div>
          </v-col>

          <!-- Address -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.address"
              label="Address*"
              outlined
              dense
              prepend-inner-icon="mdi-home"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- City -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.city"
              label="City*"
              outlined
              dense
              prepend-inner-icon="mdi-city"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- State -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.state"
              label="State*"
              outlined
              dense
              prepend-inner-icon="mdi-map-marker"
              :rules="[rules.required, rules.noXSS]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- ZIP -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="profile.zip"
              label="ZIP Code*"
              outlined
              dense
              prepend-inner-icon="mdi-mailbox"
              :rules="[rules.required, rules.zip]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>

          <!-- Age -->
          <v-col cols="12" class="py-2">
            <v-select
              :items="ageOptions"
              v-model="profile.age_group"
              label="Age Group*"
              outlined
              dense
              prepend-inner-icon="mdi-account-clock"
              :rules="[rules.required]"
              @input="onChange"
              class="modern-input"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!formValid || phonePayload.isValid === false"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import AppPhoneNumberInput from "@/components/common/AppPhoneNumberInput.vue";

export default {
  components: { AppPhoneNumberInput },
  props: {
    dialog: { type: Boolean, default: false },
    onClose: { type: Function }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      phonePayload: {},
      rules: {
        required: (value) => !!value || "Required.",
noXSS: (value) => {
  if (!value) return true;
  // Remove unnecessary escapes: " and / don't need to be escaped in [...]
  const clean = value.replace(/[<>"'/]/g, "");
  return clean === value || "Invalid characters detected";
},
        email: (value) => {
          if (!value || typeof value !== "string") return "Invalid email address";
          if (value !== value.toLowerCase()) return "Email must be in lowercase";

          const atIndex = value.lastIndexOf("@");
          if (atIndex === -1 || atIndex === 0 || atIndex === value.length - 1) return "Invalid email address";

          const local = value.substring(0, atIndex);
          const domain = value.substring(atIndex + 1);

          if (/\s/.test(value)) return "Invalid email address";

          if (
            !local ||
            local.length > 64 ||
            local.startsWith(".") ||
            local.endsWith(".") ||
            local.includes("..") ||
            !/^[a-z0-9]/.test(local) ||
            !/[a-z0-9]$/.test(local) ||
            !/^[a-z0-9._-]+$/.test(local)
          ) {
            return "Email can only contain lowercase letters, numbers, dots, underscores, and hyphens";
          }

          if (
            !domain ||
            domain.length > 253 ||
            domain.startsWith("-") ||
            domain.endsWith("-") ||
            domain.startsWith(".") ||
            domain.endsWith(".") ||
            domain.includes("..") ||
            !/^[a-z0-9.-]+$/.test(domain) ||
            !/\.[a-z]{2,}$/.test(domain)
          ) {
            return "Invalid email domain";
          }

          const domainParts = domain.split(".");
          for (const part of domainParts) {
            if (
              !part ||
              part.length > 63 ||
              part.startsWith("-") ||
              part.endsWith("-") ||
              !/^[a-z0-9-]+$/.test(part)
            ) {
              return "Invalid email domain";
            }
          }

          return true;
        },
        zip: (value) => {
          if (!value) return "ZIP code required";
          return /^[0-9]{4,10}$/.test(value) || "Invalid ZIP code";
        }
      },
      profile: {},
      ageOptions: [
        "13-18",
        "19-24",
        "25-34",
        "35-44",
        "45-54",
        "55-64",
        "65 and older"
      ]
    };
  },
  computed: {
    ...mapState("auth", { myProfile: "profile" }),
    formValid() {
      const requiredFields = [
        this.profile.first_name,
        this.profile.last_name,
        this.profile.email,
        this.profile.phone,
        this.profile.address,
        this.profile.city,
        this.profile.state,
        this.profile.zip,
        this.profile.age_group
      ].every(Boolean);

      const emailValid = this.rules.email(this.profile.email) === true;
      const phoneValid =
        this.phonePayload.isValid !== false && !!this.profile.phone;
      const zipValid = this.rules.zip(this.profile.zip) === true;

      return requiredFields && emailValid && phoneValid && zipValid;
    }
  },
  methods: {
    ...mapActions("auth", { updateProfile: "updateProfile" }),
    onChange() {
      this.message = null;
    },
    onUpdate(payload) {
      this.phonePayload = payload;
    },
    async onSave() {
      if (!this.formValid) {
        this.message = "Please complete all required fields correctly";
        this.messageType = "error";
        return;
      }

      this.loading = true;
      try {
        let phone = this.profile.phone;
        if (this.phonePayload.isValid && this.phonePayload.countryCallingCode != "1") {
          phone = `+${this.phonePayload.countryCallingCode} ${phone}`;
        }

        // Sanitize inputs before sending
        const cleanProfile = {};
        Object.keys(this.profile).forEach((key) => {
          if (typeof this.profile[key] === "string") {
            cleanProfile[key] = this.profile[key].replace(/[<>"'/]/g, "");
          } else {
            cleanProfile[key] = this.profile[key];
          }
        });

        await this.updateProfile({
          ...cleanProfile,
          phone
        });

        this.message = "Profile information updated successfully!";
        this.messageType = "success";
        setTimeout(() => this.onClose(), 1500);
      } catch (error) {
        this.message = error.response?.data?.message || "An error occurred while saving";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
    this.message = null;
  }
};
</script>


<style scoped>
.modern-profile-info-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
      background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255,255,255,0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.modern-phone-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  border: 2px solid #e0e0e0;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-profile-info-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-profile-info-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-profile-info-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-profile-info-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>